/**
 * Web Audio engine: WAV loader with caching, spatial panning, and looping.
 *
 * Sounds are referenced by path under `/sounds/...` (which is served from
 * the decoded WAV tree). The engine lazy-loads each sound the first time
 * it's needed and caches the AudioBuffer. Spatial cues (target radar,
 * incoming shell) use a StereoPannerNode for left/centre/right placement.
 */

export type SpatialPan = "left" | "center" | "right";

interface ActiveLoop {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private readonly buffers = new Map<string, Promise<AudioBuffer>>();
  private readonly loops = new Map<string, ActiveLoop>();
  /** The currently-playing voice sample (menu item, narration, intro). At
   * most one is alive at a time so new utterances cleanly interrupt the old. */
  private voiceSource: AudioBufferSourceNode | null = null;

  /** Must be called from a user-gesture handler (button click etc.). */
  async start(): Promise<void> {
    if (this.ctx) return;
    const Ctx = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) throw new Error("Web Audio API unavailable");
    this.ctx = new Ctx();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 1.0;
    this.masterGain.connect(this.ctx.destination);
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }

  setMasterVolume(v: number): void {
    if (this.masterGain) this.masterGain.gain.value = Math.max(0, Math.min(1, v));
  }

  /** Pre-fetch and decode a sound. Returns the same promise on repeat calls. */
  preload(path: string): Promise<AudioBuffer> {
    if (!this.ctx) throw new Error("AudioEngine.start() not called yet");
    const existing = this.buffers.get(path);
    if (existing) return existing;
    const p = fetch(path)
      .then((r) => {
        if (!r.ok) throw new Error(`${path}: ${r.status}`);
        return r.arrayBuffer();
      })
      .then((bytes) => this.ctx!.decodeAudioData(bytes));
    this.buffers.set(path, p);
    return p;
  }

  /** Fire-and-forget playback. Returns when started (not when finished). */
  async play(path: string, options: { pan?: SpatialPan; gain?: number } = {}): Promise<void> {
    if (!this.ctx || !this.masterGain) return;
    let buf: AudioBuffer;
    try {
      buf = await this.preload(path);
    } catch (e) {
      console.warn("audio: failed to load", path, e);
      return;
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const node = this.attachChain(src, options);
    src.connect(node);
    src.start();
  }

  /**
   * Play a one-shot sample to completion, resolving when it ends (naturally
   * or via {@link stopVoice}). Any voice sample already playing is stopped
   * first so utterances never stack.
   *
   * This is the channel for menu items, orientation cues, intro narration,
   * and other "self-voice" output. The background-loop channel is separate.
   */
  async playUntilEnd(path: string, options: { pan?: SpatialPan; gain?: number } = {}): Promise<void> {
    if (!this.ctx || !this.masterGain) return;
    this.stopVoice();
    let buf: AudioBuffer;
    try {
      buf = await this.preload(path);
    } catch (e) {
      console.warn("audio: failed to load", path, e);
      return;
    }
    return new Promise<void>((resolve) => {
      const src = this.ctx!.createBufferSource();
      src.buffer = buf;
      const node = this.attachChain(src, options);
      src.connect(node);
      src.onended = () => {
        if (this.voiceSource === src) this.voiceSource = null;
        resolve();
      };
      this.voiceSource = src;
      src.start();
    });
  }

  /** Halt the currently-playing voice sample (if any). Its promise resolves. */
  stopVoice(): void {
    const src = this.voiceSource;
    if (!src) return;
    this.voiceSource = null;
    try { src.stop(); } catch { /* already stopped */ }
  }

  /** Start (or restart) a looping ambient sound at the given path. */
  async loop(name: string, path: string, options: { gain?: number } = {}): Promise<void> {
    this.stopLoop(name);
    if (!this.ctx || !this.masterGain) return;
    let buf: AudioBuffer;
    try {
      buf = await this.preload(path);
    } catch (e) {
      console.warn("audio: loop load failed", path, e);
      return;
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const gain = this.ctx.createGain();
    gain.gain.value = options.gain ?? 0.5;
    src.connect(gain);
    gain.connect(this.masterGain);
    src.start();
    this.loops.set(name, { source: src, gain });
  }

  stopLoop(name: string): void {
    const live = this.loops.get(name);
    if (!live) return;
    try {
      live.source.stop();
    } catch {
      // already stopped
    }
    this.loops.delete(name);
  }

  /** Stop all looped sounds. */
  stopAllLoops(): void {
    for (const k of [...this.loops.keys()]) this.stopLoop(k);
  }

  private attachChain(_src: AudioBufferSourceNode, options: { pan?: SpatialPan; gain?: number }): AudioNode {
    const ctx = this.ctx!;
    const gain = ctx.createGain();
    gain.gain.value = options.gain ?? 1;
    let head: AudioNode = gain;
    if (options.pan && options.pan !== "center") {
      const panner = ctx.createStereoPanner();
      panner.pan.value = options.pan === "left" ? -0.85 : 0.85;
      gain.connect(panner);
      panner.connect(this.masterGain!);
      head = gain;
    } else {
      gain.connect(this.masterGain!);
    }
    return head;
  }
}
